/* ════════════════════════════════════════════════════════════════════════
   Chore Intelligence — derived Family → Chores matching.
   Family Profile owns personal context; Tasks own assignments.
   ARA derives suggestions; nothing here is persisted.

   Weighting (deterministic, documented):
     +3  recorded responsibility matches the task
     +2  explicit chore preference matches   (−2 if the clause is a dislike)
     +1  completed a similar task before
     +1  noticeably lighter current workload (min load, spread ≥ 2)
     −1.5 noticeably heavier workload (≥ min + 3)
     excluded: Baby always; Child for clearly unsafe tasks
   Never used: gender, pronouns, names, relationships (Parent/Child etc. are
   never matching signals — only lifeStage for broad safety).
   ════════════════════════════════════════════════════════════════════════ */

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const norm = (s) => String(s || "").toLowerCase();

const CHORE_GROUPS = {
  bins:       { label: "the bins",              words: ["bin", "bins", "rubbish", "recycling", "trash"] },
  pets:       { label: "pet care",              words: ["pet", "pets", "pet care", "dog", "cat", "litter", "chickens", "animal", "animals"] },
  cooking:    { label: "the cooking",           words: ["cook", "cooking", "dinner", "meal", "meal prep", "bake", "baking", "kitchen"] },
  laundry:    { label: "the laundry",           words: ["laundry", "washing", "fold", "folding", "clothes", "ironing"] },
  lawns:      { label: "the lawns",             words: ["lawn", "lawns", "mow", "mowing", "edges", "garden", "gardening", "weeding", "outdoor"] },
  dishes:     { label: "the dishes",            words: ["dish", "dishes", "dishwasher"] },
  cleaning:   { label: "the cleaning",          words: ["clean", "cleaning", "vacuum", "bathroom", "dust", "dusting", "mop", "mopping"] },
  organising: { label: "keeping things in order", words: ["organis", "organiz", "tidy", "tidying", "sort", "declutter"] },
  groceries:  { label: "the groceries",         words: ["grocery", "groceries", "shopping", "supermarket"] },
  school:     { label: "school runs",           words: ["school pickup", "school run", "school drop", "school administration", "school admin"] },
  admin:      { label: "the household admin",   words: ["bill", "bills", "paperwork", "admin", "appointments"] },
};

/* Clearly unsafe for young children — modest, obvious exclusions only. */
const CHILD_UNSAFE = ["oven", "chemical", "bleach", "mow", "mowing", "chainsaw", "ladder", "drill", "repair", "iron", "ironing", "stove", "knife", "knives"];

function textHits(text, words) {
  for (const w of words) {
    if (new RegExp(`\\b${escapeRe(w)}`, "i").test(text)) return true;
  }
  return false;
}

/* Which chore groups does a piece of text belong to? */
export function groupsForText(text) {
  const t = norm(text);
  if (!t.trim()) return [];
  return Object.keys(CHORE_GROUPS).filter(key => textHits(t, CHORE_GROUPS[key].words));
}

const taskText = (task) => [task.title, task.description, task.notes].filter(Boolean).join(" ");

/* Parse free-text chore preferences into signed clauses.
   "prefers kitchen jobs, hates dishes" → [{groups, sign, clean}] */
function preferenceClauses(member) {
  const raw = norm(member.living?.chorePreferences);
  if (!raw.trim()) return [];
  return raw.split(/[,;.]| but /).map(clause => {
    const groups = groupsForText(clause);
    if (groups.length === 0) return null;
    const sign = /\b(hate|hates|dislike|dislikes|avoid|avoids|not a fan|doesn't like|dont like)\b/.test(clause) ? -1 : 1;
    const clean = clause.replace(/\b(prefers?|likes?|loves?|enjoys?|hates?|dislikes?|avoids?|doesn't like|not a fan of)\b/g, "").trim();
    return { groups, sign, clean };
  }).filter(Boolean);
}

/* Active workload per member, derived from the central task list. */
export function getHouseholdChoreContext(members, tasks, excludeTaskId) {
  const counts = {};
  (members || []).forEach(m => { counts[m.id] = 0; });
  (tasks || []).forEach(t => {
    if (t.id === excludeTaskId) return;
    if (t.status === "Done" || t.status === "Completed" || t.status === "Paused") return;
    if (t.assignedTo && counts[t.assignedTo] !== undefined) counts[t.assignedTo] += 1;
  });
  const values = Object.values(counts);
  return {
    counts,
    minLoad: values.length ? Math.min(...values) : 0,
    maxLoad: values.length ? Math.max(...values) : 0,
  };
}

export function evaluateMemberForTask(member, task, ctx, tasks) {
  const text = taskText(task);
  const tGroups = groupsForText(text);
  const firstName = (member.preferredName || member.name || "").trim().split(" ")[0] || "Someone";

  /* Broad life-stage safety only — never relationship, never gender. */
  if (member.lifeStage === "Baby") return { excluded: true };
  if (member.lifeStage === "Child" && textHits(norm(text), CHILD_UNSAFE)) return { excluded: true };

  let score = 0;
  const reasons = [];

  const respGroups = new Set();
  (member.responsibilities || []).forEach(r => groupsForText(r).forEach(g => respGroups.add(g)));
  const respMatch = tGroups.find(g => respGroups.has(g));
  if (respMatch) {
    score += 3;
    reasons.push({ kind: "responsibility", text: `${firstName} usually looks after ${CHORE_GROUPS[respMatch].label}.` });
  }

  for (const clause of preferenceClauses(member)) {
    if (clause.groups.some(g => tGroups.includes(g))) {
      score += clause.sign * 2;
      if (clause.sign > 0) reasons.push({ kind: "preference", text: `${firstName} prefers ${clause.clean || "this kind of job"}.` });
      break;
    }
  }

  const doneSimilar = (tasks || []).some(t =>
    t.id !== task.id && t.status === "Done" && t.assignedTo === member.id &&
    groupsForText(taskText(t)).some(g => tGroups.includes(g))
  );
  if (doneSimilar && tGroups.length > 0) {
    score += 1;
    reasons.push({ kind: "history", text: `${firstName} has handled this one before.` });
  }

  const load = ctx.counts[member.id] ?? 0;
  if (load === ctx.minLoad && ctx.maxLoad - ctx.minLoad >= 2) {
    score += 1;
    reasons.push({ kind: "workload", text: `${firstName} has a little more room on their list today.` });
  } else if (load >= ctx.minLoad + 3) {
    score -= 1.5;
  }

  return { excluded: false, memberId: member.id, name: firstName, score, reasons };
}

/* Main entry: derive a calm, explainable suggestion for a task.
   Returns { confidence: 'strong'|'multiple'|'workload'|'none', line, suggestions } */
export function suggestMembersForTask(task, members, tasks) {
  const eligible = (members || []).filter(m => (m.name || "").trim());
  if (eligible.length === 0 || !String(task.title || "").trim()) {
    return { confidence: "none", line: null, suggestions: [] };
  }
  const ctx = getHouseholdChoreContext(eligible, tasks, task.id);
  const scored = eligible
    .map(m => evaluateMemberForTask(m, task, ctx, tasks))
    .filter(e => !e.excluded)
    .sort((a, b) => b.score - a.score);

  if (scored.length === 0) return { confidence: "none", line: null, suggestions: [] };

  const top = scored[0];
  const primaryReason = (e) => e.reasons[0]?.text || null;

  if (top.score >= 3) {
    const contenders = scored.filter(e => e.score >= 3 && e.score >= top.score - 1);
    if (contenders.length === 1) {
      return {
        confidence: "strong",
        line: primaryReason(top),
        suggestions: [{ memberId: top.memberId, name: top.name }],
      };
    }
    const [a, b] = contenders;
    return {
      confidence: "multiple",
      line: `Either ${a.name} or ${b.name} could take this one.`,
      suggestions: [{ memberId: a.memberId, name: a.name }, { memberId: b.memberId, name: b.name }],
    };
  }

  const workloadPick = scored.find(e => e.score >= 1 && e.reasons.some(r => r.kind === "workload"));
  if (workloadPick) {
    return {
      confidence: "workload",
      line: `${workloadPick.name} has a little more room on their list today.`,
      suggestions: [{ memberId: workloadPick.memberId, name: workloadPick.name }],
    };
  }

  /* Too little to go on — no invented confidence. */
  return { confidence: "none", line: "Anyone could take this one.", suggestions: [] };
}
